// Paneles de propiedades del editor de email: bloque seleccionado, fila y cuerpo.
import type { JSX, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { AlignCenter, AlignLeft, AlignRight, Bold, ImageIcon, Italic, Link2, Plus, Trash2, Underline } from "lucide-react";
import { Button, Field, Input, Select, Textarea } from "../../../components/ui.tsx";
import { MediaPicker } from "../shared/MediaPicker.tsx";
import { cleanRichHtml, isSafeHref } from "./richtext.ts";
import {
  type Align, type Block, BLOCK_LABEL, type EmailBody, FONT_OPTIONS, type FontKey, ROW_LAYOUTS, type Row, SOCIAL_META,
  type SocialNetwork, VARIABLES,
} from "./types.ts";

export function ColorInput({ label, value, onChange, allowEmpty }: { label: string; value: string | null | undefined; onChange: (v: string | null) => void; allowEmpty?: boolean }): JSX.Element {
  const [text, setText] = useState(value ?? "");
  useEffect(() => { setText(value ?? ""); }, [value]);
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input type="color" className="h-8 w-10 rounded border border-line bg-surface cursor-pointer" value={value ?? "#ffffff"} onChange={(e) => onChange(e.target.value)} aria-label={label} />
        <Input className="!py-1 text-xs font-mono" value={text} placeholder={allowEmpty ? "Sin color" : ""}
          onChange={(e) => { setText(e.target.value); if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onChange(e.target.value.toLowerCase()); else if (allowEmpty && !e.target.value) onChange(null); }} />
      </div>
    </Field>
  );
}

function AlignPicker({ value, onChange }: { value: Align; onChange: (a: Align) => void }): JSX.Element {
  const opts: Array<[Align, ReactNode, string]> = [["left", <AlignLeft key="l" size={15} />, "Izquierda"], ["center", <AlignCenter key="c" size={15} />, "Centro"], ["right", <AlignRight key="r" size={15} />, "Derecha"]];
  return (
    <Field label="Alineación">
      <div className="inline-flex rounded-md border border-line overflow-hidden">
        {opts.map(([a, icon, t]) => (
          <button key={a} type="button" title={t} aria-pressed={value === a} onClick={() => onChange(a)}
            className={`px-2.5 py-1.5 ${value === a ? "bg-primary-soft text-primary" : "text-ink-secondary hover:bg-surface-hover"}`}>{icon}</button>
        ))}
      </div>
    </Field>
  );
}

function NumberInput({ label, value, min, max, onChange, suffix }: { label: string; value: number; min: number; max: number; onChange: (n: number) => void; suffix?: string }): JSX.Element {
  return (
    <Field label={`${label}${suffix ? ` (${suffix})` : ""}`}>
      <div className="flex items-center gap-2">
        <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-[var(--primary)]" />
        <Input type="number" className="!w-16 !py-1 text-xs" min={min} max={max} value={value}
          onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, Math.round(n)))); }} />
      </div>
    </Field>
  );
}

function UrlInput({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }): JSX.Element {
  const bad = value.trim() !== "" && !isSafeHref(value);
  return (
    <Field label={label} error={bad ? "Usa una URL https://, mailto: o tel:" : undefined}>
      <Input value={value} placeholder={placeholder ?? "https://"} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

/** Editor de texto enriquecido básico (negrita, cursiva, subrayado, enlace, color, variables). */
export function RichTextEditor({ html, onChange }: { html: string; onChange: (h: string) => void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const last = useRef<string>("");
  // Solo se reescribe el contenido si cambió desde fuera (evita saltos del cursor).
  useEffect(() => {
    if (ref.current && html !== last.current) {
      const clean = cleanRichHtml(html);
      ref.current.innerHTML = clean; last.current = html;
    }
  }, [html]);
  const emit = (): void => {
    if (!ref.current) return;
    const clean = cleanRichHtml(ref.current.innerHTML);
    last.current = clean; onChange(clean);
  };
  const cmd = (name: string, value?: string): void => {
    ref.current?.focus();
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand(name, false, value);
    emit();
  };
  const btn = (title: string, icon: ReactNode, onClick: () => void): JSX.Element => (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} className="px-2 py-1.5 text-ink-secondary hover:bg-surface-hover rounded">{icon}</button>
  );
  return (
    <div className="rounded-md border border-line">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line px-1 py-0.5 bg-canvas rounded-t-md">
        {btn("Negrita", <Bold size={14} />, () => cmd("bold"))}
        {btn("Cursiva", <Italic size={14} />, () => cmd("italic"))}
        {btn("Subrayado", <Underline size={14} />, () => cmd("underline"))}
        {btn("Enlace", <Link2 size={14} />, () => {
          const u = prompt("URL del enlace (https://, mailto: o tel:)", "https://");
          if (u === null) return;
          if (!u.trim()) { cmd("unlink"); return; }
          if (!isSafeHref(u)) { alert("URL no válida."); return; }
          cmd("createLink", u.trim());
        })}
        <label className="px-1.5 py-1 cursor-pointer" title="Color del texto" onMouseDown={(e) => e.preventDefault()}>
          <input type="color" className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0" onChange={(e) => cmd("foreColor", e.target.value)} />
        </label>
        <select className="ml-auto text-xs bg-transparent text-ink-secondary border-0 focus:ring-0" value="" aria-label="Insertar variable"
          onChange={(e) => { if (e.target.value) cmd("insertText", e.target.value); e.target.value = ""; }}>
          <option value="">Variable…</option>
          {VARIABLES.filter((v) => v.key !== "{{baja_url}}").map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Texto del párrafo"
        className="min-h-[120px] max-h-[320px] overflow-y-auto p-2.5 text-sm text-ink outline-none [&_a]:text-primary [&_a]:underline"
        onInput={emit} onBlur={emit}
        onPaste={(e) => { e.preventDefault(); document.execCommand("insertText", false, e.clipboardData.getData("text/plain")); }} />
    </div>
  );
}

export function BlockProps({ block, onChange }: { block: Block; onChange: (patch: Record<string, unknown>) => void }): JSX.Element {
  const [picker, setPicker] = useState(false);
  const pad = "padding" in block ? (
    <NumberInput label="Margen interior" suffix="px" value={block.padding ?? 10} min={0} max={60} onChange={(n) => onChange({ padding: n })} />
  ) : null;
  const body = ((): ReactNode => {
    switch (block.type) {
      case "heading": return (<>
        <Field label="Texto"><Textarea rows={2} value={block.text} onChange={(e) => onChange({ text: e.target.value })} /></Field>
        <Field label="Nivel">
          <Select value={block.level} onChange={(e) => onChange({ level: Number(e.target.value) as 1 | 2 | 3 })}>
            <option value={1}>Título grande (H1)</option><option value={2}>Título (H2)</option><option value={3}>Subtítulo (H3)</option>
          </Select>
        </Field>
        <AlignPicker value={block.align} onChange={(align) => onChange({ align })} />
        <NumberInput label="Tamaño" suffix="px" value={block.fontSize ?? (block.level === 1 ? 28 : block.level === 2 ? 22 : 18)} min={12} max={60} onChange={(n) => onChange({ fontSize: n })} />
        <ColorInput label="Color" value={block.color} allowEmpty onChange={(c) => onChange({ color: c ?? undefined })} />
      </>);
      case "paragraph": return (<>
        <RichTextEditor html={block.html} onChange={(html) => onChange({ html })} />
        <AlignPicker value={block.align} onChange={(align) => onChange({ align })} />
        <NumberInput label="Tamaño" suffix="px" value={block.fontSize ?? 15} min={10} max={32} onChange={(n) => onChange({ fontSize: n })} />
        <ColorInput label="Color base" value={block.color} allowEmpty onChange={(c) => onChange({ color: c ?? undefined })} />
      </>);
      case "image": return (<>
        {block.src ? <img src={block.src} alt="" className="max-h-32 mx-auto rounded border border-line" /> : null}
        <Button small onClick={() => setPicker(true)}><ImageIcon size={14} className="mr-1 inline" />{block.src ? "Cambiar imagen" : "Elegir imagen"}</Button>
        <Field label="URL de la imagen"><Input value={block.src} placeholder="https://…" onChange={(e) => onChange({ src: e.target.value })} /></Field>
        <Field label="Texto alternativo (alt)" hint="Se muestra si la imagen no carga; importante para accesibilidad."><Input value={block.alt} onChange={(e) => onChange({ alt: e.target.value })} /></Field>
        <UrlInput label="Enlace al hacer clic (opcional)" value={block.href} placeholder="https://… (opcional)" onChange={(href) => onChange({ href })} />
        <NumberInput label="Ancho" suffix="%" value={block.width} min={10} max={100} onChange={(n) => onChange({ width: n })} />
        <AlignPicker value={block.align} onChange={(align) => onChange({ align })} />
        <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(m) => onChange({ src: m.url, ...(block.alt ? {} : { alt: (m.filename ?? "").replace(/\.[a-z]+$/i, "").replace(/[-_]+/g, " ") }) })} />
      </>);
      case "button": return (<>
        <Field label="Texto"><Input value={block.text} onChange={(e) => onChange({ text: e.target.value })} /></Field>
        <UrlInput label="URL" value={block.href} onChange={(href) => onChange({ href })} />
        <div className="grid grid-cols-2 gap-2">
          <ColorInput label="Fondo" value={block.bg} onChange={(c) => onChange({ bg: c ?? "#1e3a8a" })} />
          <ColorInput label="Texto" value={block.color} onChange={(c) => onChange({ color: c ?? "#ffffff" })} />
        </div>
        <NumberInput label="Radio de borde" suffix="px" value={block.radius} min={0} max={40} onChange={(n) => onChange({ radius: n })} />
        <NumberInput label="Tamaño de texto" suffix="px" value={block.fontSize ?? 15} min={10} max={28} onChange={(n) => onChange({ fontSize: n })} />
        <AlignPicker value={block.align} onChange={(align) => onChange({ align })} />
        <label className="flex items-center gap-2 text-sm text-ink-secondary"><input type="checkbox" checked={block.fullWidth} onChange={(e) => onChange({ fullWidth: e.target.checked })} /> Ancho completo</label>
      </>);
      case "divider": return (<>
        <ColorInput label="Color" value={block.color} onChange={(c) => onChange({ color: c ?? "#e5e7eb" })} />
        <NumberInput label="Grosor" suffix="px" value={block.thickness} min={1} max={10} onChange={(n) => onChange({ thickness: n })} />
        <Field label="Estilo">
          <Select value={block.style} onChange={(e) => onChange({ style: e.target.value as "solid" })}>
            <option value="solid">Continuo</option><option value="dashed">Discontinuo</option><option value="dotted">Punteado</option>
          </Select>
        </Field>
        <NumberInput label="Ancho" suffix="%" value={block.width} min={10} max={100} onChange={(n) => onChange({ width: n })} />
      </>);
      case "spacer": return <NumberInput label="Altura" suffix="px" value={block.height} min={4} max={200} onChange={(n) => onChange({ height: n })} />;
      case "qr": return (<>
        <Field label="Contenido del QR" hint="Normalmente una URL. Máx. 500 caracteres."><Textarea rows={2} maxLength={500} value={block.data} onChange={(e) => onChange({ data: e.target.value })} /></Field>
        <NumberInput label="Tamaño" suffix="px" value={block.size} min={80} max={300} onChange={(n) => onChange({ size: n })} />
        <ColorInput label="Color" value={block.color} onChange={(c) => onChange({ color: c ?? "#000000" })} />
        <AlignPicker value={block.align} onChange={(align) => onChange({ align })} />
      </>);
      case "html": return (
        <Field label="HTML" hint="Se eliminan scripts, formularios, iframes y atributos de eventos al guardar.">
          <Textarea rows={12} className="font-mono text-xs" value={block.html} onChange={(e) => onChange({ html: e.target.value })} />
        </Field>
      );
      case "social": return (<>
        {block.items.map((it, i) => (
          <div key={i} className="flex items-end gap-1.5">
            <div className="w-28 shrink-0">
              <Field label={i === 0 ? "Red" : undefined}>
                <Select value={it.network} onChange={(e) => onChange({ items: block.items.map((x, j) => (j === i ? { ...x, network: e.target.value as SocialNetwork } : x)) })}>
                  {(Object.keys(SOCIAL_META) as SocialNetwork[]).map((n) => <option key={n} value={n}>{SOCIAL_META[n].name}</option>)}
                </Select>
              </Field>
            </div>
            <div className="flex-1"><Field label={i === 0 ? "URL" : undefined}><Input value={it.url} onChange={(e) => onChange({ items: block.items.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} /></Field></div>
            <button type="button" className="btn-ghost btn-sm mb-0.5" title="Quitar" onClick={() => onChange({ items: block.items.filter((_, j) => j !== i) })}><Trash2 size={13} /></button>
          </div>
        ))}
        {block.items.length < 10 && <Button small onClick={() => onChange({ items: [...block.items, { network: "web", url: "https://" }] })}><Plus size={13} className="mr-1 inline" />Añadir red</Button>}
        <NumberInput label="Tamaño de icono" suffix="px" value={block.size} min={20} max={48} onChange={(n) => onChange({ size: n })} />
        <AlignPicker value={block.align} onChange={(align) => onChange({ align })} />
      </>);
    }
  })();
  return (
    <div className="space-y-3">
      <div className="sec-title">{BLOCK_LABEL[block.type]}</div>
      {body}
      {pad}
    </div>
  );
}

export function RowProps({ row, onChange, onLayout }: { row: Row; onChange: (patch: Partial<Row>) => void; onLayout: (ratios: number[]) => void }): JSX.Element {
  const key = row.ratios.join(":");
  return (
    <div className="space-y-3">
      <div className="sec-title">Fila ({row.columns.length} {row.columns.length === 1 ? "columna" : "columnas"})</div>
      <Field label="Distribución de columnas">
        <Select value={ROW_LAYOUTS.some((l) => l.ratios.join(":") === key) ? key : ""} onChange={(e) => { const l = ROW_LAYOUTS.find((x) => x.ratios.join(":") === e.target.value); if (l) onLayout(l.ratios); }}>
          {!ROW_LAYOUTS.some((l) => l.ratios.join(":") === key) && <option value="">Personalizada ({key})</option>}
          {ROW_LAYOUTS.map((l) => <option key={l.label} value={l.ratios.join(":")}>{l.label}</option>)}
        </Select>
      </Field>
      {row.columns.length > 1 && (
        <Field label="Proporciones (1-12 por columna)">
          <div className="flex gap-1.5">
            {row.ratios.map((r, i) => (
              <Input key={i} type="number" min={1} max={12} className="!py-1 text-xs" value={r}
                onChange={(e) => { const n = Math.max(1, Math.min(12, Math.round(Number(e.target.value) || 1))); onChange({ ratios: row.ratios.map((x, j) => (j === i ? n : x)) }); }} />
            ))}
          </div>
        </Field>
      )}
      <ColorInput label="Fondo de la fila" value={row.bg ?? null} allowEmpty onChange={(c) => onChange({ bg: c })} />
      <NumberInput label="Margen interior" suffix="px" value={row.padding} min={0} max={60} onChange={(n) => onChange({ padding: n })} />
      {row.columns.length > 1 && (
        <label className="flex items-center gap-2 text-sm text-ink-secondary">
          <input type="checkbox" checked={row.stackOnMobile !== false} onChange={(e) => onChange({ stackOnMobile: e.target.checked })} /> Apilar columnas en móvil
        </label>
      )}
    </div>
  );
}

export function BodyProps({ body, onChange }: { body: EmailBody; onChange: (patch: Partial<EmailBody>) => void }): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="sec-title">Cuerpo del correo</div>
      <NumberInput label="Ancho del contenido" suffix="px" value={body.width} min={500} max={700} onChange={(n) => onChange({ width: n })} />
      <Field label="Fuente">
        <Select value={body.font} onChange={(e) => onChange({ font: e.target.value as FontKey })}>
          {FONT_OPTIONS.map((f) => <option key={f.key} value={f.key} style={{ fontFamily: f.css }}>{f.label}</option>)}
        </Select>
      </Field>
      <Field label="Alineación del contenido">
        <Select value={body.align} onChange={(e) => onChange({ align: e.target.value as "left" | "center" })}>
          <option value="center">Centrado</option><option value="left">Izquierda</option>
        </Select>
      </Field>
      <ColorInput label="Color de texto" value={body.textColor} onChange={(c) => onChange({ textColor: c ?? "#1f2937" })} />
      <ColorInput label="Color de enlaces" value={body.linkColor} onChange={(c) => onChange({ linkColor: c ?? "#1e3a8a" })} />
      <ColorInput label="Fondo exterior" value={body.bg} onChange={(c) => onChange({ bg: c ?? "#f1f5f9" })} />
      <ColorInput label="Fondo del contenido" value={body.contentBg} onChange={(c) => onChange({ contentBg: c ?? "#ffffff" })} />
      <p className="text-xs text-ink-muted">Variables disponibles en textos: {VARIABLES.map((v) => v.key).join(", ")}. El pie con el enlace de baja se añade automáticamente si no usas {"{{baja_url}}"}.</p>
    </div>
  );
}
